<template>
  <div
    :style="{
      ...$store.state.event.goldcastThemeStyles,
      ...$store.state.event.themeStyles
    }"
    :class="recordingMode && 'hidden-component'"
  >
    <template v-if="showing">
      <div :class="['dialog-wrapper', blockBackgroundActions ? 'blockBackgroundActions' : 'accessBackground']">
        <div
          ref="dialog"
          :role="role"
          :aria-label="role === 'none' ? undefined : resolvedAriaLabel"
          :aria-labelledby="role === 'none' ? undefined : ariaLabelledby || undefined"
          :aria-describedby="role === 'none' ? undefined : ariaDescribedby || undefined"
          :aria-modal="isModal ? 'true' : undefined"
          :tabindex="isModal ? -1 : undefined"
          :style="{
            minWidth: 0,
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: '100vh',
            overflowX: 'hidden',
            overflowY: 'auto'
          }"
          :class="[
            'dialog-component',
            placement,
            blockBackgroundActions ? 'blockBackgroundActions' : 'accessBackground'
          ]"
        >
          <div v-if="!hideOverlay" role="none" class="bg" @click="tryToClose"></div>
          <component
            :is="component"
            :class="['component', !blockBackgroundActions && 'allowActions']"
            v-bind="props"
            @hide="hide"
            @cancel="cancel"
            @showSuccess="showSuccess"
          />
          <template v-if="successVisible">
            <div class="overlay" />
            <div class="dialog-notify">
              <div class="success-block">
                <span class="success-text">{{ successText }}</span>
                {{ successMessage }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import loggerService from '../logger.service';

const inertElementState = new WeakMap();

function acquireInert(element) {
  const state = inertElementState.get(element) || {
    count: 0,
    originallyInert: element.hasAttribute('inert')
  };
  state.count += 1;
  inertElementState.set(element, state);
  element.setAttribute('inert', '');
}

function releaseInert(element) {
  const state = inertElementState.get(element);
  if (!state) {
    return;
  }

  state.count -= 1;
  if (state.count > 0) {
    return;
  }

  if (!state.originallyInert) {
    element.removeAttribute('inert');
  }
  inertElementState.delete(element);
}

export default {
  name: 'dialog-component',
  provide() {
    return {
      dialogSemanticOwner: true
    };
  },
  props: {
    component: {
      type: Object,
      required: true
    },
    props: {
      type: Object,
      default: () => ({})
    },
    hideOverlay: {
      type: Boolean,
      default: false
    },
    cancelOnClickOutside: Boolean,
    cancelOnEscape: {
      type: Boolean,
      default: true
    },
    autoCancelTimeout: Number,
    placement: {
      type: String,
      default: 'center-center'
    },
    blockBackgroundActions: {
      type: Boolean,
      default: true
    },
    role: {
      type: String,
      default: 'dialog',
      validator: value => ['dialog', 'alertdialog', 'none'].includes(value)
    },
    ariaLabel: String,
    ariaLabelledby: String,
    ariaDescribedby: String,
    restoreFocusTo: {
      type: Object,
      default: null
    },
    shouldRestoreFocus: {
      type: Function,
      default: () => true
    },
    shouldHandleAction: {
      type: Function,
      default: () => true
    }
  },
  data() {
    return {
      showing: true,
      successVisible: false,
      successMessage: '',
      successText: 'SUCCESS !!',
      previouslyFocusedElement: null,
      autoCancelTimer: null,
      closeTimer: null,
      inertedElements: []
    };
  },
  mounted() {
    if (this.isModal) {
      this.previouslyFocusedElement = this.restoreFocusTo || document.activeElement;
    }
    this.$refs.dialog.addEventListener('keydown', this.handleKeydown);
    if (this.isModal) {
      this.acquireModalInertState();
      this.$nextTick(this.focusInitialElement);
    }

    if (this.autoCancelTimeout) {
      this.autoCancelTimer = setTimeout(() => {
        this.autoCancelTimer = null;
        loggerService.warn(`Dialog auto cancelled, timeout of ${this.autoCancelTimeout}ms`);
        this.cancel();
      }, this.autoCancelTimeout);
    }
  },
  beforeUnmount() {
    this.$refs.dialog?.removeEventListener('keydown', this.handleKeydown);
    this.clearAutoCancelTimer();
    this.clearCloseTimer();
    this.releaseModalInertState();
  },
  computed: {
    isModal() {
      return this.blockBackgroundActions;
    },
    recordingMode() {
      return !!this.$route?.query?.record_mode;
    },
    resolvedAriaLabel() {
      if (this.ariaLabelledby) {
        return undefined;
      }

      return this.ariaLabel || this.props?.title || this.props?.heading || this.$t('@@translate@@ Dialog');
    }
  },
  methods: {
    acquireModalInertState() {
      const popperRoot = this.$el.closest('#component-popper-wrapper');
      const backgroundElements = popperRoot
        ? Array.from(popperRoot.parentElement?.children || []).filter(element => element !== popperRoot)
        : [];
      let dialogHost = this.$el;
      while (dialogHost.parentElement && dialogHost.parentElement !== popperRoot) {
        dialogHost = dialogHost.parentElement;
      }
      const coveredPopups =
        popperRoot && dialogHost.parentElement === popperRoot
          ? Array.from(popperRoot.children).filter(element => element !== dialogHost)
          : [];
      this.inertedElements = [...backgroundElements, ...coveredPopups];
      this.inertedElements.forEach(acquireInert);
    },
    releaseModalInertState() {
      this.inertedElements.forEach(releaseInert);
      this.inertedElements = [];
    },
    hide(reason) {
      if (!this.shouldHandleAction('onHide')) {
        return;
      }
      loggerService.info('Dialog hidden', { reason });
      this.waitForAnimation(() => this.$emit('hide', reason));
    },
    cancel(reason) {
      if (!this.shouldHandleAction('onCancel')) {
        return;
      }
      this.closeAsCancel(reason);
    },
    requestClose(reason) {
      this.closeAsCancel(reason);
    },
    closeAsCancel(reason) {
      loggerService.info('Dialog cancelled', { reason });
      try {
        this.waitForAnimation(() => this.$emit('cancel', reason));
      } catch (e) {
        loggerService.warn(e ?? 'Error Caught');
      }
    },
    showSuccess(successText, successMessage) {
      this.successMessage = successMessage;
      this.successText = successText;
      this.successVisible = true;
      setTimeout(() => {
        this.successVisible = false;
        this.cancel('');
      }, 1500);
    },
    waitForAnimation(cb) {
      if (!this.showing) {
        return;
      }

      this.clearAutoCancelTimer();
      this.showing = false;
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        this.releaseModalInertState();
        this.restoreFocus();
        cb();
      }, 350);
    },
    tryToClose() {
      if (this.cancelOnClickOutside) {
        this.cancel();
      }
    },
    getFocusableElements() {
      const dialog = this.$refs.dialog;
      if (!dialog) {
        return [];
      }

      return Array.from(
        dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(element => this.isRenderedFocusable(element, dialog));
    },
    isRenderedFocusable(element, dialog) {
      if (
        element.matches(':disabled, input[type="hidden"], [aria-disabled="true"]') ||
        element.tabIndex < 0 ||
        element.closest('[hidden], [inert], [aria-hidden="true"]')
      ) {
        return false;
      }

      let currentElement = element;
      while (currentElement && currentElement !== dialog.parentElement) {
        const style = window.getComputedStyle(currentElement);
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false;
        }
        if (currentElement === dialog) {
          break;
        }
        currentElement = currentElement.parentElement;
      }

      return true;
    },
    focusInitialElement() {
      const firstFocusableElement = this.getFocusableElements()[0];
      const dialog = this.$el.querySelector('[role="dialog"], [role="alertdialog"]');
      (firstFocusableElement || dialog)?.focus();
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (this.cancelOnEscape) {
          this.cancel();
        }
        return;
      }

      if (event.key !== 'Tab' || !this.isModal) {
        return;
      }

      const focusableElements = this.getFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        event.currentTarget.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    },
    clearAutoCancelTimer() {
      if (this.autoCancelTimer === null) {
        return;
      }

      clearTimeout(this.autoCancelTimer);
      this.autoCancelTimer = null;
    },
    clearCloseTimer() {
      if (this.closeTimer === null) {
        return;
      }

      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    },
    restoreFocus() {
      if (this.isModal && this.shouldRestoreFocus() && this.previouslyFocusedElement?.isConnected) {
        this.previouslyFocusedElement.focus();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.hidden-component {
  display: none;
}
.dialog-wrapper {
  --fixed-margin-x: 16px;
  --fixed-margin-y: 16px;
  --sidebar-width: 390px;

  .edge {
    --fixed-margin-x: 0px;
    --fixed-margin-y: 0px;
  }
  .bottom-right {
    right: var(--fixed-margin-x);
    bottom: var(--fixed-margin-y);
  }
  .bottom-left {
    left: var(--fixed-margin-x);
    bottom: var(--fixed-margin-y);
  }
  .top-left {
    left: var(--fixed-margin-x);
    top: var(--fixed-margin-y);
  }
  .top-right {
    right: var(--fixed-margin-x);
    top: var(--fixed-margin-y);
  }
  .center-center {
    width: 100vw;
    height: 100vh;
    left: 0;
    top: 0;
  }
  .stage-center {
    width: calc(100vw - var(--sidebar-width));
    height: 100vh;
    left: 0;
    top: 0;
  }
  .dialog-component {
    position: fixed;
    z-index: 55;
    opacity: 1;
    border-radius: 20px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    max-width: calc(100vw - 2rem);
    max-height: calc(100dvh - 2rem);
    overflow-x: hidden;
    overflow-y: auto;
  }

  .dialog-component > .component {
    flex-shrink: 0;
    margin-block: auto;
    min-inline-size: 0;
    overflow-wrap: anywhere;
  }

  & .blockBackgroundActions {
    pointer-events: all;
  }

  & .accessBackground {
    pointer-events: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s;
}

.fade-enter,
.fade-leave-to {
  transform: scale(0);
  opacity: 0;
}

.bg {
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;
  background: rgba(0, 0, 0, 0.4);
}
.component.allowActions {
  pointer-events: all;
}

.overlay {
  width: 100%;
  height: 100%;
  position: absolute;
  background: black;
  opacity: 50%;
  bottom: 0;
  border-radius: 20px;
  z-index: 27;
}

.dialog-notify {
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  justify-content: center;
  border-radius: 20px;
}

.success-block {
  position: fixed;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  font-size: 14px;
  top: 40%;
  width: auto;
  animation: notify ease 0.5s;
  -webkit-animation: notify ease 0.5s;
  -moz-animation: notify ease 0.5s;
  -o-animation: notify ease 0.5s;
  z-index: 12;
  /* box-shadow: 0 4px 5px 0 #eff3f3; */
}

.success-text {
  font-size: 20px;
  color: #2ebd59;
  font-weight: 600;
  margin: 0 0 10px 0;
}

@keyframes notify {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@-moz-keyframes notify {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@-webkit-keyframes notify {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@-o-keyframes notify {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@-ms-keyframes notify {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
@media only screen and (max-width: 600px) {
  .dialog-wrapper {
    --sidebar-width: 0px;
  }
}
</style>
